/* /sales/returns/:id — the return detail page (RET-1). Mirrors `orders/Page.js` exactly: this
   file owns only the id, the request and the loading/error/not-found states; `ReturnDetail` is
   the presentational component, later reusable in a drawer over the list.

   `id` is a UUID string from Excalibur, never coerced to a number. */

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Alert as AntAlert, Button, Empty, Flex, Skeleton, Typography } from "antd";

import CounterpartySection from "../../component/CounterpartySection";
import { RETURNS_PAGE } from "../../config/lang";
import { call, isOk } from "../../util/action";
import { translate } from "../../util/util";

import ReturnDetail, { ReturnDetailHead, ReturnGroups } from "./ReturnDetail";

const Page = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language || "en");
  const t = key => translate(RETURNS_PAGE, key, lang);

  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error | not_found

  /* Same 404-vs-error split as `orders/Page.js`: a DRF `{"detail": "Not found."}` body passes
     `isOk()` (no HTTP status survives on it), so the actual marker is the missing `id`. */
  const load = useCallback(() => {
    setStatus("loading");
    call({ method: "GET", url: `excalibur/returns/${id}/` }, response => {
      if (!isOk(response)) return setStatus("error");
      if (!response?.id) return setStatus("not_found");
      setItem(response);
      setStatus("ready");
    });
  }, [id]);

  useEffect(load, [load]);

  if (status === "loading") {
    return (
      <Flex vertical gap="middle" className="shell-content">
        <Skeleton active paragraph={{ rows: 8 }} />
      </Flex>
    );
  }

  if (status === "error") {
    return (
      <Flex vertical gap="middle" className="shell-content">
        <AntAlert
          type="error"
          showIcon
          message={t("error_title")}
          action={
            <Button size="small" onClick={load}>
              {t("retry")}
            </Button>
          }
        />
      </Flex>
    );
  }

  if (status === "not_found" || !item) {
    return (
      <Flex vertical gap="middle" className="shell-content">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={4} align="center">
              <Typography.Text strong>{t("not_found_title")}</Typography.Text>
              <Typography.Text type="secondary">{t("not_found_desc")}</Typography.Text>
            </Flex>
          }>
          {/* A page opened from the counterparty card's Returns tab names its own way back in
              `state.from` (an address object: the card's tab and its route state), which wins. */}
          <Button onClick={() => history.push(location.state?.from || { pathname: "/sales/returns", search: location.search })}>{t("back_to_list")}</Button>
        </Empty>
      </Flex>
    );
  }

  return (
    <div className="shell-content">
      <ReturnDetailHead item={item} lang={lang} />
      {/* Archetype B's form+rail grid (`detail-page.scss`, shared — imported once from
          `src/index.js`, no new import needed here). No `GeoSection` here: `tracked_geolocation`
          exists only on `Order`, so a return's rail is the counterparty card alone.
          `dp-grid--fluid`: same reasoning as `orders/Page.js` — the main column grows with the
          viewport instead of sitting fixed at 720px. Opt-in modifier, `/points`/`/import`/`/task`
          are unaffected. `dp-grid--stack-1280`: below 1280px the rail goes under the items, as on
          the order page. */}
      <div className="dp-grid dp-grid--fluid dp-grid--stack-1280">
        <div className="dp-main">
          <ReturnDetail item={item} lang={lang} showHeader={false} layout="page" />
        </div>
        <aside className="dp-rail">
          {/* "Деталі" (with the point), then "Контрагент": the same card `/sales/orders/:id` puts in
              its rail, and deliberately the same component — the backend nests one
              `CounterpartyDetailSerializer` under both detail endpoints (see
              `component/CounterpartySection.js`). */}
          <ReturnGroups item={item} lang={lang} place="page" />
          <CounterpartySection item={item} lang={lang} group={RETURNS_PAGE} actionKey="counterparty_all_returns_btn" listPath="/sales/returns" />
        </aside>
      </div>
    </div>
  );
};

export default Page;
